import React, { useState, useEffect, useRef } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useCompany } from '../contexts/CompanyContext';
import { sessionNoteService } from '../services/SessionNoteService';
import { claudeAIService } from '../services/ClaudeAIService';
import { supervisionTrackingService } from '../services/SupervisionTrackingService';
import { pdfExportService } from '../services/PDFExportService';
import { storageService } from '../services/StorageService';
import { BillingEntry } from '../components/BillingEntry';
import { SupervisorComplianceChecklist, ComplianceChecklistData } from '../components/SupervisorComplianceChecklist';
import { TreatmentInformation, TreatmentInformationData } from '../components/TreatmentInformation';
import { AiAssistedNoteSection } from '../components/AiAssistedNoteSection';
import { SessionNote, SupervisionMethod } from '../models/SessionNote';
import { Timestamp } from 'firebase/firestore';
import { FileText, Clock, CheckCircle, Edit3, Save, Send, Loader, Eye, Download, Camera, MapPin } from 'lucide-react';
import { Modal } from '../components/common/Modal';

const ReviewNotes: React.FC = () => {
  const { user } = useAuth();
  const { company } = useCompany();

  const [notesNeedingReview, setNotesNeedingReview] = useState<SessionNote[]>([]);
  const [selectedNote, setSelectedNote] = useState<SessionNote | null>(null);
  const [loading, setLoading] = useState(true);

  // AI Enhancement
  const [processingAI, setProcessingAI] = useState(false);
  const [aiError, setAiError] = useState<string | null>(null);
  const [aiSummaryGenerated, setAiSummaryGenerated] = useState(false);

  // Supervisor editing
  const [clinicalNotes, setClinicalNotes] = useState('');
  const [liveStreamObservations, setLiveStreamObservations] = useState('');
  const [supervisionFeedback, setSupervisionFeedback] = useState('');
  const [recommendationsForRBT, setRecommendationsForRBT] = useState('');

  // Supervision data
  const [billingCode, setBillingCode] = useState('97155');
  const [supervisionMethod, setSupervisionMethod] = useState<SupervisionMethod>('live-stream');
  const [supervisionNotes, setSupervisionNotes] = useState('');
  const [supervisionStartTime, setSupervisionStartTime] = useState<Timestamp | null>(null);
  const [supervisionDuration, setSupervisionDuration] = useState(0);

  // Automatic supervision tracking
  const [currentViewingLogId, setCurrentViewingLogId] = useState<string | null>(null);
  const [totalViewingTime, setTotalViewingTime] = useState(0);

  // Signature
  const [showSignature, setShowSignature] = useState(false);
  const [signatureName, setSignatureName] = useState('');
  const [signatureCredentials, setSignatureCredentials] = useState('');
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const [isDrawing, setIsDrawing] = useState(false);

  // Messages
  const [successMessage, setSuccessMessage] = useState<string | null>(null);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  // PDF Export
  const [exportingPDF, setExportingPDF] = useState(false);

  // Billing, Compliance, and Treatment Info
  const [complianceComplete, setComplianceComplete] = useState(false);
  const [billingComplete, setBillingComplete] = useState(false);
  const [treatmentInfoComplete, setTreatmentInfoComplete] = useState(false);

  // Timer for supervision duration
  useEffect(() => {
    let interval: NodeJS.Timeout;
    if (supervisionStartTime && selectedNote) {
      interval = setInterval(() => {
        const now = Timestamp.now();
        const duration = Math.floor((now.seconds - supervisionStartTime.seconds) / 60); // minutes
        setSupervisionDuration(duration);
      }, 1000);
    }
    return () => clearInterval(interval);
  }, [supervisionStartTime, selectedNote]);

  useEffect(() => {
    if (user && (user.role === 'supervisor' || user.role === 'admin')) {
      loadNotesNeedingReview();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user]);

  // Cleanup: End viewing session when component unmounts or user navigates away
  useEffect(() => {
    return () => {
      if (selectedNote && currentViewingLogId) {
        supervisionTrackingService.endViewingSession(
          selectedNote.id,
          currentViewingLogId
        );
      }
    };
  }, [selectedNote, currentViewingLogId]);

  useEffect(() => {
    if (user) {
      setSignatureName(user.name);
      setSignatureCredentials(user.credentials || '');
    }
  }, [user]);

  const loadNotesNeedingReview = async () => {
    if (!user) return;

    setLoading(true);
    try {
      const notes = await sessionNoteService.getSessionNotesNeedingReview(user.id);
      setNotesNeedingReview(notes);
    } catch (err) {
      console.error('Error loading notes:', err);
      setErrorMessage('Failed to load notes needing review');
    } finally {
      setLoading(false);
    }
  };

  const handleSelectNote = async (note: SessionNote) => {
    if (!user) return;

    // If switching notes, end the current viewing session
    if (selectedNote && selectedNote.id !== note.id && currentViewingLogId) {
      await supervisionTrackingService.endViewingSession(
        selectedNote.id,
        currentViewingLogId
      );
      setCurrentViewingLogId(null);
    }

    setSelectedNote(note);
    setAiError(null);
    setAiSummaryGenerated(!!note.aiEnhanced);

    // Start supervision timer
    if (!note.timestamps.supervisorReviewStarted) {
      await sessionNoteService.startSupervisorReview(note.id, user.id);
      setSupervisionStartTime(Timestamp.now());
    } else {
      setSupervisionStartTime(note.timestamps.supervisorReviewStarted);
    }

    // Start automatic viewing session tracking
    try {
      const viewingLogId = await supervisionTrackingService.startViewingSession(
        note.id,
        user.id,
        user.name,
        supervisionMethod,
        billingCode
      );
      setCurrentViewingLogId(viewingLogId);
    } catch (error) {
      console.error('Error starting viewing session:', error);
    }

    // If AI hasn't processed yet and we have a draft, trigger AI enhancement
    if (note.status === 'ai-processing' && note.rbtDraft && !note.aiEnhanced) {
      await handleAIEnhancement(note);
    }

    // Load AI-enhanced content if available
    if (note.aiEnhanced) {
      setClinicalNotes(note.aiEnhanced.professionalSummary + '\n\n' + note.aiEnhanced.programNotes + '\n\n' + note.aiEnhanced.behaviorAnalysis);
    }

    // Load supervisor's previous work if exists
    if (note.supervisorFinal) {
      setClinicalNotes(note.supervisorFinal.clinicalNotes);
      setLiveStreamObservations(note.supervisorFinal.liveStreamObservations);
      setSupervisionFeedback(note.supervisorFinal.supervisionFeedback);
      setRecommendationsForRBT(note.supervisorFinal.recommendationsForRBT);
    }

    // Load supervision data if exists
    if (note.supervision) {
      setBillingCode(note.supervision.billingCode);
      setSupervisionMethod(note.supervision.supervisionMethod);
      setSupervisionNotes(note.supervision.supervisionNotes);
      setTotalViewingTime(note.supervision.totalViewingTime || 0);
    }

    // Check if billing, compliance, and treatment info are already completed
    setComplianceComplete(!!note.complianceChecklist);
    setBillingComplete(!!note.billing);
    setTreatmentInfoComplete(!!note.treatmentInfo);
  };

  const handleAIEnhancement = async (note?: SessionNote) => {
    const noteToEnhance = note || selectedNote;
    if (!noteToEnhance || !noteToEnhance.rbtDraft) return;

    setProcessingAI(true);
    setAiError(null);

    try {
      // Call secure backend function to enhance notes
      // Backend will validate permissions, call Claude API, and save results (return value unused — we re-fetch below)
      await claudeAIService.enhanceSessionNotes(noteToEnhance.id);

      // Refresh the note to get the saved AI enhancement
      const updated = await sessionNoteService.getSessionNote(noteToEnhance.id);
      if (updated) {
        setSelectedNote(updated);
        if (updated.aiEnhanced) {
          // Build a comprehensive clinical summary
          const summary = [
            '=== PROFESSIONAL SUMMARY ===',
            updated.aiEnhanced.professionalSummary,
            '',
            '=== PROGRAM NOTES ===',
            updated.aiEnhanced.programNotes,
            '',
            '=== BEHAVIOR ANALYSIS ===',
            updated.aiEnhanced.behaviorAnalysis,
            '',
            '=== CLINICAL OBSERVATIONS ===',
            updated.aiEnhanced.clinicalObservations,
          ].join('\n');

          setClinicalNotes(summary);
          setAiSummaryGenerated(true);
        }
      }

      setSuccessMessage('AI summary generated! You can edit the notes before approval.');
    } catch (err: any) {
      console.error('Error enhancing with AI:', err);
      setAiError(err.message || 'Failed to generate AI summary');
    } finally {
      setProcessingAI(false);
    }
  };

  const handleSaveDraft = async () => {
    if (!user || !selectedNote) return;

    try {
      await sessionNoteService.saveSupervisorFinal(
        selectedNote.id,
        {
          clinicalNotes,
          liveStreamObservations,
          supervisionFeedback,
          recommendationsForRBT,
        },
        user.id
      );

      setSuccessMessage('Draft saved successfully!');
    } catch (err) {
      console.error('Error saving draft:', err);
      setErrorMessage('Failed to save draft');
    }
  };

  const handleComplianceComplete = async (data: ComplianceChecklistData) => {
    if (!user || !selectedNote) return;

    try {
      await sessionNoteService.updateSessionNote(
        selectedNote.id,
        {
          complianceChecklist: {
            ...data,
            completedAt: Timestamp.now(),
          },
        },
        user.id
      );

      setComplianceComplete(true);
      setSuccessMessage('Compliance checklist completed successfully!');

      // Refresh the note to get the updated data
      const updated = await sessionNoteService.getSessionNote(selectedNote.id);
      if (updated) {
        setSelectedNote(updated);
      }
    } catch (err) {
      console.error('Error saving compliance checklist:', err);
      setErrorMessage('Failed to save compliance checklist');
    }
  };

  const handleTreatmentInfoComplete = async (data: TreatmentInformationData) => {
    if (!user || !selectedNote) return;

    try {
      await sessionNoteService.updateSessionNote(
        selectedNote.id,
        {
          treatmentInfo: {
            ...data,
            completedAt: Timestamp.now(),
          },
        },
        user.id
      );

      setTreatmentInfoComplete(true);
      setSuccessMessage('Treatment information completed successfully!');

      // Refresh the note to get the updated data
      const updated = await sessionNoteService.getSessionNote(selectedNote.id);
      if (updated) {
        setSelectedNote(updated);
      }
    } catch (err) {
      console.error('Error saving treatment information:', err);
      setErrorMessage('Failed to save treatment information');
    }
  };

  const handleBillingComplete = async (billingData: {
    code: string;
    placeOfService: string;
    locationModifier?: string;
    startTime: Date;
    endTime: Date;
    duration: number;
    unitsOfService: number;
  }) => {
    if (!user || !selectedNote) return;

    try {
      await sessionNoteService.updateSessionNote(
        selectedNote.id,
        {
          billing: {
            ...billingData,
            startTime: Timestamp.fromDate(billingData.startTime),
            endTime: Timestamp.fromDate(billingData.endTime),
            providerTaxId: user.taxId,
          },
        },
        user.id
      );

      setBillingComplete(true);
      setSuccessMessage('Billing information saved successfully!');

      // Refresh the note to get the updated data
      const updated = await sessionNoteService.getSessionNote(selectedNote.id);
      if (updated) {
        setSelectedNote(updated);
      }
    } catch (err) {
      console.error('Error saving billing information:', err);
      setErrorMessage('Failed to save billing information');
    }
  };

  const handleSubmitForSignature = async () => {
    if (!user || !selectedNote) return;

    // Validate that treatment info, compliance, and billing are complete
    if (!treatmentInfoComplete) {
      setErrorMessage('Please complete the treatment information before submitting for signature');
      return;
    }

    if (!complianceComplete) {
      setErrorMessage('Please complete the compliance checklist before submitting for signature');
      return;
    }

    if (!billingComplete) {
      setErrorMessage('Please complete the billing entry before submitting for signature');
      return;
    }

    try {
      // End current viewing session before finalizing
      if (currentViewingLogId) {
        await supervisionTrackingService.endViewingSession(
          selectedNote.id,
          currentViewingLogId,
          'Submitted for signature'
        );
        setCurrentViewingLogId(null);
      }

      // Save supervisor final notes
      await sessionNoteService.saveSupervisorFinal(
        selectedNote.id,
        {
          clinicalNotes,
          liveStreamObservations,
          supervisionFeedback,
          recommendationsForRBT,
        },
        user.id
      );

      // Calculate supervision duration
      const endTime = Timestamp.now();
      const duration = supervisionStartTime
        ? Math.floor((endTime.seconds - supervisionStartTime.seconds) / 60)
        : 0;

      // Save supervision data (including all viewing logs)
      await sessionNoteService.saveSupervisionData(
        selectedNote.id,
        {
          billingCode,
          supervisionStartTime: supervisionStartTime!,
          supervisionEndTime: endTime,
          supervisionDuration: duration,
          supervisionMethod,
          supervisionNotes,
        },
        user.id
      );

      setShowSignature(true);
    } catch (err) {
      console.error('Error submitting for signature:', err);
      setErrorMessage('Failed to submit for signature');
    }
  };

  const handleSignature = async () => {
    if (!user || !selectedNote || !canvasRef.current) return;

    try {
      // Get signature image
      const signatureImage = canvasRef.current.toDataURL('image/png');

      // Add supervisor signature
      await sessionNoteService.addSignature(
        selectedNote.id,
        'supervisor',
        {
          signedBy: signatureName,
          credentials: signatureCredentials,
          signatureImage,
        },
        user.id
      );

      // Get the updated note with signature for PDF generation
      const updatedNote = await sessionNoteService.getSessionNote(selectedNote.id);

      if (updatedNote) {
        // Generate and upload PDF to storage
        try {
          const companyName = company?.name || 'Behavioral Health Services';
          const companyLogo = company?.logoUrl;
          const pdfBlob = await pdfExportService.generatePDFBlob(updatedNote, companyName, companyLogo);

          // Get session date from timestamps
          const sessionDate = updatedNote.timestamps.sessionStart
            ? (updatedNote.timestamps.sessionStart as any).toDate()
            : new Date();

          // Upload to client-focused storage structure
          const pdfUrl = await storageService.uploadApprovedPdf(
            pdfBlob,
            updatedNote.clientId,
            updatedNote.id,
            sessionDate
          );

          // Save PDF URL to the session note
          await sessionNoteService.updateSessionNote(updatedNote.id, {
            approvedPdfUrl: pdfUrl,
            timestamps: {
              ...updatedNote.timestamps,
              pdfGenerated: Timestamp.now()
            }
          }, user.id);

          console.log('PDF uploaded to storage:', pdfUrl);
        } catch (pdfErr) {
          // PDF upload failed but signature was saved - log but don't block
          console.error('Error uploading PDF to storage:', pdfErr);
          // User can still export manually
        }
      }

      setSuccessMessage('Session note approved and finalized!');
      setShowSignature(false);

      // Refresh list and clear selection
      await loadNotesNeedingReview();
      setSelectedNote(null);
    } catch (err) {
      console.error('Error saving signature:', err);
      setErrorMessage('Failed to save signature');
    }
  };

  // Canvas drawing functions
  const startDrawing = (e: React.MouseEvent<HTMLCanvasElement>) => {
    setIsDrawing(true);
    const canvas = canvasRef.current;
    if (!canvas) return;

    const rect = canvas.getBoundingClientRect();
    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    ctx.beginPath();
    ctx.moveTo(e.clientX - rect.left, e.clientY - rect.top);
  };

  const draw = (e: React.MouseEvent<HTMLCanvasElement>) => {
    if (!isDrawing) return;

    const canvas = canvasRef.current;
    if (!canvas) return;

    const rect = canvas.getBoundingClientRect();
    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    ctx.lineTo(e.clientX - rect.left, e.clientY - rect.top);
    ctx.strokeStyle = '#000';
    ctx.lineWidth = 2;
    ctx.stroke();
  };

  const stopDrawing = () => {
    setIsDrawing(false);
  };

  const clearSignature = () => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    ctx.clearRect(0, 0, canvas.width, canvas.height);
  };

  const handleExportPDF = async () => {
    if (!selectedNote) return;

    setExportingPDF(true);
    setErrorMessage(null);

    try {
      const companyName = company?.name || 'Behavioral Health Services';
      const companyLogo = company?.logoUrl;

      await pdfExportService.exportSessionNoteToPDF(
        selectedNote,
        companyName,
        companyLogo
      );

      setSuccessMessage('PDF exported successfully!');
    } catch (err: any) {
      console.error('Error exporting PDF:', err);
      setErrorMessage(err.message || 'Failed to export PDF. Please try again.');
    } finally {
      setExportingPDF(false);
    }
  };

  if (!user || (user.role !== 'supervisor' && user.role !== 'admin')) {
    return (
      <div style={{ padding: 'var(--spacing-8)', textAlign: 'center' }}>
        <p style={{ color: 'var(--color-gray-600)' }}>
          This page is only accessible to supervisors.
        </p>
      </div>
    );
  }

  return (
    <div style={{ padding: 'var(--spacing-6)', height: '100vh', display: 'flex', flexDirection: 'column' }}>
      <div style={{ marginBottom: 'var(--spacing-6)' }}>
        <h1 style={{ fontSize: 'var(--text-3xl)', fontWeight: 'bold', marginBottom: 'var(--spacing-2)' }}>
          <FileText style={{ display: 'inline', marginRight: 'var(--spacing-2)' }} />
          Review Session Notes
        </h1>
        <p style={{ color: 'var(--color-gray-600)' }}>
          Review and approve RBT session documentation
        </p>
      </div>

      {successMessage && (
        <div style={{
          backgroundColor: 'var(--color-success-50)',
          border: '1px solid var(--color-success-200)',
          color: 'var(--color-success-800)',
          padding: 'var(--spacing-4)',
          borderRadius: 'var(--radius-lg)',
          marginBottom: 'var(--spacing-4)',
        }}>
          {successMessage}
        </div>
      )}

      {errorMessage && (
        <div style={{
          backgroundColor: 'var(--color-error-50)',
          border: '1px solid var(--color-error-200)',
          color: 'var(--color-error-800)',
          padding: 'var(--spacing-4)',
          borderRadius: 'var(--radius-lg)',
          marginBottom: 'var(--spacing-4)',
        }}>
          {errorMessage}
        </div>
      )}

      <div style={{ display: 'grid', gridTemplateColumns: selectedNote ? '280px 1fr' : '1fr', gap: 'var(--spacing-6)', flex: 1, minHeight: 0 }}>
        {/* Left Panel: Notes List + Check-In Verification */}
        <div style={{
          display: 'flex',
          flexDirection: 'column',
          gap: 'var(--spacing-4)',
          overflowY: 'auto',
        }}>
          {/* Pending Review List */}
          <div style={{
            backgroundColor: 'white',
            border: '1px solid var(--color-gray-300)',
            borderRadius: 'var(--radius-lg)',
            padding: '16px',
          }}>
            <h2 style={{ fontSize: '18px', fontWeight: 'bold', marginBottom: '16px', color: '#1f2937' }}>
              Pending Review ({notesNeedingReview.length})
            </h2>

            {loading ? (
              <div style={{ textAlign: 'center', padding: '32px' }}>
                <Loader className="animate-spin" style={{ margin: '0 auto' }} />
              </div>
            ) : notesNeedingReview.length === 0 ? (
              <p style={{ color: '#6b6b6b', textAlign: 'center', padding: '24px' }}>
                <CheckCircle style={{ display: 'block', margin: '0 auto 12px' }} size={48} color="#16a34a" />
                All caught up!
              </p>
            ) : (
              <div style={{ display: 'grid', gap: '10px' }}>
                {notesNeedingReview.map(note => (
                  <div
                    key={note.id}
                    onClick={() => handleSelectNote(note)}
                    style={{
                      padding: '14px',
                      border: selectedNote?.id === note.id ? '2px solid #4F6367' : '1px solid #d1d5db',
                      borderRadius: '8px',
                      cursor: 'pointer',
                      backgroundColor: selectedNote?.id === note.id ? '#eff6ff' : 'white',
                      transition: 'all 0.15s ease',
                    }}
                    onMouseEnter={(e) => {
                      if (selectedNote?.id !== note.id) {
                        e.currentTarget.style.backgroundColor = '#f9fafb';
                        e.currentTarget.style.borderColor = '#9ca3af';
                      }
                    }}
                    onMouseLeave={(e) => {
                      if (selectedNote?.id !== note.id) {
                        e.currentTarget.style.backgroundColor = 'white';
                        e.currentTarget.style.borderColor = '#d1d5db';
                      }
                    }}
                  >
                    <div style={{ fontWeight: 'bold', marginBottom: '6px', fontSize: '15px', color: '#1f2937' }}>
                      {note.rbtName}
                    </div>
                    <div style={{ fontSize: '14px', color: '#6b6b6b' }}>
                      {note.timestamps.rbtDraftSubmitted?.toDate().toLocaleDateString()}
                    </div>
                    <div style={{
                      fontSize: '13px',
                      color: note.status === 'ai-processing' ? '#d97706' : '#059669',
                      marginTop: '6px',
                      fontWeight: '500',
                    }}>
                      {note.status === 'ai-processing' ? 'Processing…' : 'Ready for review'}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Check-In Verification - Moved to left panel */}
          {selectedNote && (selectedNote.photoVerification || selectedNote.checkInLocation) && (
            <div style={{
              backgroundColor: 'white',
              border: '1px solid var(--color-gray-300)',
              borderRadius: 'var(--radius-lg)',
              padding: 'var(--spacing-3)',
            }}>
              <h3 style={{
                fontSize: 'var(--text-sm)',
                fontWeight: 'bold',
                marginBottom: 'var(--spacing-3)',
                color: 'var(--color-primary)',
                display: 'flex',
                alignItems: 'center',
                gap: 'var(--spacing-2)',
              }}>
                <Camera size={16} />
                Check-In Verification
              </h3>

              {/* Photo - Smaller */}
              {selectedNote.photoVerification && (
                <div style={{ marginBottom: 'var(--spacing-3)' }}>
                  <img
                    src={selectedNote.photoVerification.photoUrl}
                    alt="RBT check-in"
                    style={{
                      width: '100%',
                      maxWidth: '200px',
                      borderRadius: 'var(--radius-md)',
                      border: '2px solid var(--color-gray-200)',
                    }}
                  />
                  <div style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '4px',
                    marginTop: 'var(--spacing-2)',
                    fontSize: 'var(--text-xs)',
                    color: 'var(--color-success-700)',
                  }}>
                    <CheckCircle size={12} />
                    Photo Verified
                  </div>
                </div>
              )}

              {/* GPS Info - Compact */}
              {selectedNote.checkInLocation && (
                <div style={{
                  fontSize: 'var(--text-xs)',
                  color: 'var(--color-gray-600)',
                  backgroundColor: 'var(--color-gray-50)',
                  padding: 'var(--spacing-2)',
                  borderRadius: 'var(--radius-sm)',
                }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '4px', marginBottom: '4px' }}>
                    <MapPin size={12} />
                    <strong>GPS Location</strong>
                  </div>
                  <div>Lat: {selectedNote.checkInLocation.latitude.toFixed(6)}</div>
                  <div>Lng: {selectedNote.checkInLocation.longitude.toFixed(6)}</div>
                  <div>Accuracy: {selectedNote.checkInLocation.accuracy.toFixed(0)}m</div>
                </div>
              )}

              {/* Timestamp */}
              {selectedNote.photoVerification?.capturedAt && (
                <div style={{
                  fontSize: 'var(--text-xs)',
                  color: 'var(--color-gray-500)',
                  marginTop: 'var(--spacing-2)',
                }}>
                  {selectedNote.photoVerification.capturedAt.toDate().toLocaleString()}
                </div>
              )}
            </div>
          )}
        </div>

        {/* Right Panel: Review Interface */}
        {selectedNote && (
          <div style={{
            display: 'flex',
            flexDirection: 'column',
            gap: 'var(--spacing-4)',
            overflowY: 'auto',
            paddingRight: 'var(--spacing-2)',
          }}>
            {/* Section 1: RBT's Original Notes */}
            <div style={{
              backgroundColor: 'white',
              border: '1px solid var(--color-gray-300)',
              borderRadius: 'var(--radius-lg)',
              padding: '24px',
            }}>
              <h3 style={{ fontSize: '22px', fontWeight: 'bold', marginBottom: '20px', color: '#4F6367' }}>
                RBT's Original Notes
              </h3>

              {selectedNote.rbtDraft && (
                <>
                  <div style={{ marginBottom: '20px' }}>
                    <h4 style={{ fontWeight: 'bold', fontSize: '16px', marginBottom: '10px', color: '#374151' }}>
                      Session Summary
                    </h4>
                    <p style={{ fontSize: '15px', color: '#4b5563', lineHeight: '1.6', backgroundColor: '#f9fafb', padding: '14px', borderRadius: '8px' }}>
                      {selectedNote.rbtDraft.sessionSummary}
                    </p>
                  </div>

                  {selectedNote.rbtDraft.quickNotes.length > 0 && (
                    <div style={{ marginBottom: '20px' }}>
                      <h4 style={{ fontWeight: 'bold', fontSize: '16px', marginBottom: '10px', color: '#374151' }}>
                        Quick Notes
                      </h4>
                      <div style={{ backgroundColor: '#f9fafb', padding: '14px', borderRadius: '8px' }}>
                        {selectedNote.rbtDraft.quickNotes.map((note, i) => (
                          <div key={i} style={{ fontSize: '14px', color: '#4b5563', marginBottom: '8px', paddingBottom: '8px', borderBottom: i < selectedNote.rbtDraft!.quickNotes.length - 1 ? '1px solid #e5e0db' : 'none' }}>
                            <span style={{ fontWeight: '600', color: '#6b6b6b' }}>[{note.timestamp.toDate().toLocaleTimeString()}]</span> {note.content}
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  {selectedNote.rbtDraft.programData.length > 0 && (
                    <div style={{ marginBottom: '20px' }}>
                      <h4 style={{ fontWeight: 'bold', fontSize: '16px', marginBottom: '10px', color: '#374151' }}>
                        Program Data
                      </h4>
                      {selectedNote.rbtDraft.programData.map((program, i) => (
                        <div key={i} style={{ fontSize: '15px', marginBottom: '12px', padding: '14px', backgroundColor: '#f0f9ff', borderRadius: '8px', border: '1px solid #bae6fd' }}>
                          <div style={{ fontWeight: 'bold', fontSize: '16px', color: '#0369a1', marginBottom: '6px' }}>{program.programName}</div>
                          <div style={{ fontSize: '14px', color: '#4b5563' }}>
                            <strong>Trials:</strong> {program.trialsCompleted} | <strong>Correct:</strong> {program.correctResponses} | <strong>Prompt Level:</strong> {program.promptLevel}
                          </div>
                          {program.notes && <div style={{ fontSize: '14px', marginTop: '8px', color: '#6b6b6b', fontStyle: 'italic' }}>{program.notes}</div>}
                        </div>
                      ))}
                    </div>
                  )}

                  {selectedNote.rbtDraft.behaviorData.length > 0 && (
                    <div style={{ marginBottom: '20px' }}>
                      <h4 style={{ fontWeight: 'bold', fontSize: '16px', marginBottom: '10px', color: '#374151' }}>
                        Behavior Data
                      </h4>
                      {selectedNote.rbtDraft.behaviorData.map((behavior, i) => (
                        <div key={i} style={{ fontSize: '15px', marginBottom: '12px', padding: '14px', backgroundColor: '#fef3c7', borderRadius: '8px', border: '1px solid #fcd34d' }}>
                          <div style={{ fontWeight: 'bold', fontSize: '16px', color: '#92400e', marginBottom: '6px' }}>{behavior.behaviorName} <span style={{ fontWeight: 'normal', fontSize: '14px' }}>(Frequency: {behavior.frequency})</span></div>
                          <div style={{ fontSize: '14px', color: '#4b5563' }}>
                            <strong>Antecedents:</strong> {behavior.antecedents}
                          </div>
                          <div style={{ fontSize: '14px', color: '#4b5563', marginTop: '4px' }}>
                            <strong>Consequences:</strong> {behavior.consequences}
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </>
              )}
            </div>

            {/* AI-Assisted Note (Phase 9.3 + 9.4): ABA template draft + dual summaries */}
            {selectedNote && (
              <AiAssistedNoteSection
                selectedNote={selectedNote}
                onUpdate={(updated) => setSelectedNote(updated)}
                onUseAsClinicalNotes={(content) => setClinicalNotes(content)}
              />
            )}

            {/* Section 2: Enhanced Notes (Editable) */}
            <div style={{
              backgroundColor: 'white',
              border: '1px solid var(--color-gray-300)',
              borderRadius: 'var(--radius-lg)',
              padding: 'var(--spacing-6)',
            }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 'var(--spacing-4)' }}>
                <h3 style={{ fontSize: '22px', fontWeight: 'bold', color: 'var(--color-success-600)' }}>
                  <Edit3 size={22} style={{ display: 'inline', marginRight: '8px', verticalAlign: 'middle' }} />
                  Supervisor Notes
                </h3>

                {/* Generate AI Summary Button */}
                {!processingAI && selectedNote?.rbtDraft && (
                  <button
                    onClick={() => handleAIEnhancement()}
                    disabled={processingAI}
                    style={{
                      backgroundColor: aiSummaryGenerated ? '#10b981' : '#8b5cf6',
                      color: 'white',
                      padding: '12px 20px',
                      borderRadius: '8px',
                      border: 'none',
                      cursor: 'pointer',
                      fontWeight: 'bold',
                      fontSize: '15px',
                      display: 'flex',
                      alignItems: 'center',
                      gap: '8px',
                      boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
                      transition: 'all 0.2s ease',
                    }}
                    onMouseEnter={(e) => {
                      e.currentTarget.style.transform = 'scale(1.02)';
                      e.currentTarget.style.boxShadow = '0 4px 8px rgba(0,0,0,0.15)';
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.transform = 'scale(1)';
                      e.currentTarget.style.boxShadow = '0 2px 4px rgba(0,0,0,0.1)';
                    }}
                  >
                    <FileText size={18} />
                    {aiSummaryGenerated ? 'Regenerate AI Summary' : 'Generate AI Summary'}
                  </button>
                )}
              </div>

              {processingAI && (
                <div style={{
                  textAlign: 'center',
                  padding: '40px 20px',
                  backgroundColor: '#f5f3ff',
                  borderRadius: '12px',
                  border: '2px dashed #8b5cf6',
                }}>
                  <Loader className="animate-spin" size={40} style={{ margin: '0 auto', color: '#8b5cf6' }} />
                  <p style={{ marginTop: '16px', color: '#6b21a8', fontSize: '16px', fontWeight: '500' }}>
                    AI is generating your clinical summary...
                  </p>
                  <p style={{ marginTop: '8px', color: '#9ca3af', fontSize: '14px' }}>
                    This may take 10-20 seconds
                  </p>
                </div>
              )}

              {aiError && (
                <div style={{
                  backgroundColor: '#fef2f2',
                  border: '1px solid #fecaca',
                  color: '#dc2626',
                  padding: '16px',
                  borderRadius: '8px',
                  marginBottom: '16px',
                  fontSize: '15px',
                }}>
                  <strong>Error:</strong> {aiError}
                </div>
              )}

              {!processingAI && (
                <>
                  {/* AI Summary Info Banner */}
                  {!aiSummaryGenerated && !clinicalNotes.trim() && (
                    <div style={{
                      backgroundColor: '#f5f3ff',
                      border: '1px solid #c4b5fd',
                      borderRadius: '8px',
                      padding: '16px',
                      marginBottom: '16px',
                      display: 'flex',
                      alignItems: 'flex-start',
                      gap: '12px',
                    }}>
                      <FileText size={24} style={{ color: '#8b5cf6', flexShrink: 0, marginTop: '2px' }} />
                      <div>
                        <p style={{ fontSize: '15px', fontWeight: '600', color: '#6b21a8', marginBottom: '4px' }}>
                          AI Summary Available
                        </p>
                        <p style={{ fontSize: '14px', color: '#7c3aed' }}>
                          Click "Generate AI Summary" to create a professional clinical summary from the RBT's notes.
                          You can edit the generated summary before approval.
                        </p>
                      </div>
                    </div>
                  )}

                  <div style={{ marginBottom: '20px' }}>
                    <label style={{ display: 'block', fontWeight: 'bold', fontSize: '16px', marginBottom: '8px', color: '#374151' }}>
                      Clinical Notes {aiSummaryGenerated && <span style={{ color: '#8b5cf6', fontWeight: 'normal', fontSize: '14px' }}>(AI-Generated - Editable)</span>}
                    </label>
                    <textarea
                      value={clinicalNotes}
                      onChange={(e) => setClinicalNotes(e.target.value)}
                      placeholder="Enter clinical observations and notes, or click 'Generate AI Summary' to auto-generate..."
                      rows={12}
                      style={{
                        width: '100%',
                        padding: '16px',
                        border: aiSummaryGenerated ? '2px solid #8b5cf6' : '1px solid #d1d5db',
                        borderRadius: '8px',
                        fontSize: '15px',
                        lineHeight: '1.6',
                        resize: 'vertical',
                        fontFamily: 'inherit',
                      }}
                    />
                  </div>

                  <div style={{ marginBottom: '20px' }}>
                    <label style={{ display: 'block', fontWeight: 'bold', fontSize: '16px', marginBottom: '8px', color: '#374151' }}>
                      Live Stream Observations
                    </label>
                    <textarea
                      value={liveStreamObservations}
                      onChange={(e) => setLiveStreamObservations(e.target.value)}
                      placeholder="Additional observations from live stream viewing..."
                      rows={5}
                      style={{
                        width: '100%',
                        padding: '14px',
                        border: '1px solid #d1d5db',
                        borderRadius: '8px',
                        fontSize: '15px',
                        lineHeight: '1.5',
                        resize: 'vertical',
                        fontFamily: 'inherit',
                      }}
                    />
                  </div>

                  <div style={{ marginBottom: '20px' }}>
                    <label style={{ display: 'block', fontWeight: 'bold', fontSize: '16px', marginBottom: '8px', color: '#374151' }}>
                      Supervision Feedback
                    </label>
                    <textarea
                      value={supervisionFeedback}
                      onChange={(e) => setSupervisionFeedback(e.target.value)}
                      placeholder="Feedback for RBT performance..."
                      rows={4}
                      style={{
                        width: '100%',
                        padding: '14px',
                        border: '1px solid #d1d5db',
                        borderRadius: '8px',
                        fontSize: '15px',
                        lineHeight: '1.5',
                        resize: 'vertical',
                        fontFamily: 'inherit',
                      }}
                    />
                  </div>

                  <div style={{ marginBottom: '20px' }}>
                    <label style={{ display: 'block', fontWeight: 'bold', fontSize: '16px', marginBottom: '8px', color: '#374151' }}>
                      Recommendations for RBT
                    </label>
                    <textarea
                      value={recommendationsForRBT}
                      onChange={(e) => setRecommendationsForRBT(e.target.value)}
                      placeholder="Recommendations for future sessions..."
                      rows={4}
                      style={{
                        width: '100%',
                        padding: '14px',
                        border: '1px solid #d1d5db',
                        borderRadius: '8px',
                        fontSize: '15px',
                        lineHeight: '1.5',
                        resize: 'vertical',
                        fontFamily: 'inherit',
                      }}
                    />
                  </div>

                  <div style={{ display: 'flex', gap: 'var(--spacing-2)' }}>
                    <button
                      onClick={handleSaveDraft}
                      style={{
                        flex: 1,
                        backgroundColor: 'var(--color-gray-600)',
                        color: 'white',
                        padding: 'var(--spacing-3)',
                        borderRadius: 'var(--radius-md)',
                        border: 'none',
                        cursor: 'pointer',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        gap: 'var(--spacing-2)',
                      }}
                    >
                      <Save size={18} />
                      Save Draft
                    </button>
                  </div>
                </>
              )}
            </div>

            {/* Section 3: Treatment Information */}
            <div style={{
              backgroundColor: 'white',
              border: '1px solid var(--color-gray-300)',
              borderRadius: 'var(--radius-lg)',
              padding: '24px',
            }}>
              {!treatmentInfoComplete ? (
                <TreatmentInformation
                  onComplete={handleTreatmentInfoComplete}
                  initialData={selectedNote.treatmentInfo}
                  rbtBehaviorData={selectedNote.rbtDraft?.behaviorData?.map(b => ({
                    behaviorName: b.behaviorName,
                    frequency: b.frequency,
                    antecedents: b.antecedents,
                    consequences: b.consequences,
                  }))}
                />
              ) : (
                <div style={{
                  padding: '16px',
                  backgroundColor: '#f0fdf4',
                  border: '2px solid #86efac',
                  borderRadius: '8px',
                }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '12px' }}>
                    <CheckCircle size={24} style={{ color: '#16a34a' }} />
                    <span style={{ fontSize: '18px', fontWeight: 'bold', color: '#166534' }}>
                      Treatment Information Completed
                    </span>
                  </div>
                  {selectedNote.treatmentInfo && (
                    <div style={{ fontSize: '14px', color: '#4b5563' }}>
                      <p><strong>Client joined:</strong> {selectedNote.treatmentInfo.clientJoinedSession}</p>
                      <p><strong>Behaviors present:</strong> {selectedNote.treatmentInfo.behaviorsPresent ? 'Yes' : 'No'}</p>
                      <p><strong>Procedures correct:</strong> {selectedNote.treatmentInfo.proceduresUsedCorrectly ? 'Yes' : 'No'}</p>
                      {selectedNote.treatmentInfo.sessionGoals.length > 0 && (
                        <p><strong>Goals:</strong> {selectedNote.treatmentInfo.sessionGoals.join(', ')}</p>
                      )}
                    </div>
                  )}
                </div>
              )}
            </div>

            {/* Section 4: Supervision Data & Signature */}
            <div style={{
              backgroundColor: 'white',
              border: '1px solid var(--color-gray-300)',
              borderRadius: 'var(--radius-lg)',
              padding: 'var(--spacing-6)',
            }}>
              <h3 style={{ fontSize: 'var(--text-xl)', fontWeight: 'bold', marginBottom: 'var(--spacing-4)', color: 'var(--color-warning-600)' }}>
                Supervision, Billing & Approval
              </h3>

              <div style={{ marginBottom: 'var(--spacing-4)' }}>
                <label style={{ display: 'block', fontWeight: 'bold', fontSize: 'var(--text-sm)', marginBottom: 'var(--spacing-2)' }}>
                  Billing Code
                </label>
                <input
                  type="text"
                  value={billingCode}
                  onChange={(e) => setBillingCode(e.target.value)}
                  style={{
                    width: '100%',
                    padding: 'var(--spacing-2)',
                    border: '1px solid var(--color-gray-300)',
                    borderRadius: 'var(--radius-md)',
                  }}
                />
              </div>

              <div style={{ marginBottom: 'var(--spacing-4)' }}>
                <label style={{ display: 'block', fontWeight: 'bold', fontSize: 'var(--text-sm)', marginBottom: 'var(--spacing-2)' }}>
                  Supervision Method
                </label>
                <select
                  value={supervisionMethod}
                  onChange={(e) => setSupervisionMethod(e.target.value as SupervisionMethod)}
                  style={{
                    width: '100%',
                    padding: 'var(--spacing-2)',
                    border: '1px solid var(--color-gray-300)',
                    borderRadius: 'var(--radius-md)',
                  }}
                >
                  <option value="live-stream">Live Stream</option>
                  <option value="in-person">In Person</option>
                  <option value="recording-review">Recording Review</option>
                </select>
              </div>

              <div style={{ marginBottom: 'var(--spacing-4)' }}>
                <label style={{ display: 'block', fontWeight: 'bold', fontSize: 'var(--text-sm)', marginBottom: 'var(--spacing-2)' }}>
                  <Clock size={14} style={{ display: 'inline', marginRight: '4px' }} />
                  Current Session Duration
                </label>
                <div style={{ fontSize: 'var(--text-2xl)', fontWeight: 'bold', color: 'var(--color-primary-600)' }}>
                  {supervisionDuration} minutes
                </div>
              </div>

              {totalViewingTime > 0 && (
                <div style={{ marginBottom: 'var(--spacing-4)' }}>
                  <label style={{ display: 'block', fontWeight: 'bold', fontSize: 'var(--text-sm)', marginBottom: 'var(--spacing-2)' }}>
                    <Eye size={14} style={{ display: 'inline', marginRight: '4px' }} />
                    Total Viewing Time
                  </label>
                  <div style={{ fontSize: 'var(--text-lg)', fontWeight: 'bold', color: 'var(--color-success-600)' }}>
                    {supervisionTrackingService.formatViewingTime(totalViewingTime)}
                  </div>
                  <div style={{ fontSize: 'var(--text-xs)', color: 'var(--color-gray-600)', marginTop: '4px' }}>
                    Across all viewing sessions
                  </div>
                </div>
              )}

              <div style={{ marginBottom: 'var(--spacing-4)' }}>
                <label style={{ display: 'block', fontWeight: 'bold', fontSize: 'var(--text-sm)', marginBottom: 'var(--spacing-2)' }}>
                  Supervision Notes
                </label>
                <textarea
                  value={supervisionNotes}
                  onChange={(e) => setSupervisionNotes(e.target.value)}
                  placeholder="Additional supervision notes..."
                  rows={4}
                  style={{
                    width: '100%',
                    padding: 'var(--spacing-3)',
                    border: '1px solid var(--color-gray-300)',
                    borderRadius: 'var(--radius-md)',
                    fontSize: 'var(--text-sm)',
                    resize: 'vertical',
                  }}
                />
              </div>

              {/* Compliance Checklist */}
              {!complianceComplete && (
                <div style={{ marginBottom: 'var(--spacing-4)' }}>
                  <SupervisorComplianceChecklist
                    onComplete={handleComplianceComplete}
                    initialData={selectedNote.complianceChecklist}
                  />
                </div>
              )}

              {complianceComplete && (
                <div style={{
                  marginBottom: 'var(--spacing-4)',
                  padding: 'var(--spacing-3)',
                  backgroundColor: 'var(--color-success-50)',
                  border: '1px solid var(--color-success-200)',
                  borderRadius: 'var(--radius-md)',
                }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--spacing-2)' }}>
                    <CheckCircle size={16} style={{ color: 'var(--color-success-600)' }} />
                    <span style={{ fontSize: 'var(--text-sm)', fontWeight: 'bold', color: 'var(--color-success-900)' }}>
                      Compliance Checklist Completed
                    </span>
                  </div>
                </div>
              )}

              {/* Billing Entry */}
              {complianceComplete && !billingComplete && user && (
                <div style={{ marginBottom: 'var(--spacing-4)' }}>
                  <BillingEntry
                    providerName={user.name}
                    providerCredentials={user.credentials || ''}
                    sessionStartTime={selectedNote.timestamps?.sessionStart?.toDate()}
                    sessionEndTime={selectedNote.timestamps?.sessionEnd?.toDate()}
                    onComplete={handleBillingComplete}
                    initialData={selectedNote.billing ? {
                      code: selectedNote.billing.code as any,
                      placeOfService: selectedNote.billing.placeOfService as any,
                      locationModifier: selectedNote.billing.locationModifier as any,
                      startTime: selectedNote.billing.startTime?.toDate(),
                      endTime: selectedNote.billing.endTime?.toDate(),
                    } : undefined}
                  />
                </div>
              )}

              {billingComplete && (
                <div style={{
                  marginBottom: 'var(--spacing-4)',
                  padding: 'var(--spacing-3)',
                  backgroundColor: 'var(--color-success-50)',
                  border: '1px solid var(--color-success-200)',
                  borderRadius: 'var(--radius-md)',
                }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--spacing-2)' }}>
                    <CheckCircle size={16} style={{ color: 'var(--color-success-600)' }} />
                    <span style={{ fontSize: 'var(--text-sm)', fontWeight: 'bold', color: 'var(--color-success-900)' }}>
                      Billing Information Completed
                    </span>
                  </div>
                </div>
              )}

              {selectedNote.status === 'approved' && selectedNote.signatures?.supervisor ? (
                <button
                  onClick={handleExportPDF}
                  disabled={exportingPDF}
                  style={{
                    width: '100%',
                    backgroundColor: exportingPDF ? 'var(--color-gray-400)' : 'var(--color-primary-600)',
                    color: 'white',
                    padding: 'var(--spacing-3)',
                    borderRadius: 'var(--radius-md)',
                    border: 'none',
                    cursor: exportingPDF ? 'not-allowed' : 'pointer',
                    fontWeight: 'bold',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    gap: 'var(--spacing-2)',
                  }}
                >
                  {exportingPDF ? (
                    <>
                      <Loader size={18} className="animate-spin" />
                      Generating PDF...
                    </>
                  ) : (
                    <>
                      <Download size={18} />
                      Export PDF
                    </>
                  )}
                </button>
              ) : (
                <>
                  {(!treatmentInfoComplete || !complianceComplete || !billingComplete) && (
                    <div style={{
                      marginBottom: 'var(--spacing-3)',
                      padding: 'var(--spacing-3)',
                      backgroundColor: 'var(--color-warning-50)',
                      border: '1px solid var(--color-warning-200)',
                      borderRadius: 'var(--radius-md)',
                      fontSize: 'var(--text-xs)',
                      color: 'var(--color-warning-900)',
                    }}>
                      <strong>Required before signature:</strong>
                      <ul style={{ marginTop: 'var(--spacing-1)', paddingLeft: 'var(--spacing-4)' }}>
                        {!treatmentInfoComplete && <li>Complete treatment information</li>}
                        {!complianceComplete && <li>Complete compliance checklist</li>}
                        {!billingComplete && <li>Complete billing entry</li>}
                      </ul>
                    </div>
                  )}
                  <button
                    onClick={handleSubmitForSignature}
                    disabled={!clinicalNotes.trim() || !treatmentInfoComplete || !complianceComplete || !billingComplete}
                    style={{
                      width: '100%',
                      backgroundColor: (!clinicalNotes.trim() || !treatmentInfoComplete || !complianceComplete || !billingComplete) ? 'var(--color-gray-400)' : 'var(--color-success-600)',
                      color: 'white',
                      padding: 'var(--spacing-3)',
                      borderRadius: 'var(--radius-md)',
                      border: 'none',
                      cursor: (!clinicalNotes.trim() || !treatmentInfoComplete || !complianceComplete || !billingComplete) ? 'not-allowed' : 'pointer',
                      fontWeight: 'bold',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      gap: 'var(--spacing-2)',
                    }}
                  >
                    <Send size={18} />
                    Sign & Approve
                  </button>
                </>
              )}
            </div>
          </div>
        )}
      </div>

      {/* Signature Modal */}
      <Modal
        isOpen={showSignature}
        onClose={() => setShowSignature(false)}
        title="Digital Signature"
        size="md"
        showCloseButton={false}
      >
        <div className="space-y-6">
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
            <p className="text-sm text-blue-900">
              By signing this document, you certify that the information contained in this session note is accurate and complete to the best of your knowledge.
            </p>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Name
            </label>
            <input
              type="text"
              value={signatureName}
              onChange={(e) => setSignatureName(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Credentials
            </label>
            <input
              type="text"
              value={signatureCredentials}
              onChange={(e) => setSignatureCredentials(e.target.value)}
              placeholder="e.g., BCBA, LBA, MSPsych"
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Signature
            </label>
            <canvas
              ref={canvasRef}
              width={500}
              height={150}
              onMouseDown={startDrawing}
              onMouseMove={draw}
              onMouseUp={stopDrawing}
              onMouseLeave={stopDrawing}
              className="w-full border-2 border-gray-300 rounded-lg cursor-crosshair bg-white"
              style={{ touchAction: 'none' }}
            />
            <button
              onClick={clearSignature}
              className="mt-2 text-sm text-blue-600 hover:text-blue-800 underline"
            >
              Clear Signature
            </button>
          </div>

          <div className="flex gap-3 pt-4 border-t border-gray-200">
            <button
              onClick={() => setShowSignature(false)}
              className="flex-1 px-4 py-3 border border-gray-300 bg-white text-gray-700 rounded-lg hover:bg-gray-50 font-medium transition-colors"
            >
              Cancel
            </button>
            <button
              onClick={handleSignature}
              disabled={!signatureName.trim() || !signatureCredentials.trim()}
              className="flex-1 px-4 py-3 bg-green-600 text-white rounded-lg hover:bg-green-700 font-medium disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
            >
              Approve & Finalize
            </button>
          </div>
        </div>
      </Modal>
    </div>
  );
};

export default ReviewNotes;
